import { useEffect, useState } from "react";
import { api } from "../lib/api";
import CrudTable from "../components/CrudTable";
import PDFButton from "../components/PDFButton";
import Calendar from "../components/Calendar";
import { Modal } from "../components/ui";
import ImportInductionAttendees from "../components/ImportInductionAttendees";
import { Upload, Users } from "lucide-react";

const TABS = [
  { key: "calendar", label: "Calendario" },
  { key: "talks", label: "Pláticas Ambientales" },
  { key: "ephemerides", label: "Efemérides" },
  { key: "inductions", label: "Inducción Ambiental" },
  { key: "team_members", label: "Equipo Ambiental" },
];

const fieldsByColl = {
  talks: [
    { key: "topic", label: "Tema", required: true },
    { key: "date", label: "Fecha", type: "date" },
    { key: "time", label: "Horario" },
    { key: "presenter", label: "Presentador" },
    { key: "status", label: "Status", type: "select", options: ["Programada", "Completada", "Cancelada"] },
  ],
  ephemerides: [
    { key: "topic", label: "Tema", required: true },
    { key: "publish_date", label: "Fecha publicación", type: "date" },
    { key: "status", label: "Status", type: "select", options: ["Borrador", "Aprobado", "Publicado", "Enviado"] },
  ],
  inductions: [
    { key: "code", label: "# (IND-AAAA-###)" },
    { key: "title", label: "Tema/Título", required: true },
    { key: "date", label: "Fecha", type: "date" },
    { key: "time", label: "Horario" },
    { key: "place", label: "Lugar" },
    { key: "mode", label: "Modalidad", type: "select", options: ["Presencial", "Virtual", "Híbrida"] },
    { key: "presenter", label: "Impartido por" },
    { key: "status", label: "Status", type: "select", options: ["Programada", "Completada", "Cancelada", "Reprogramada"] },
    { key: "notes", label: "Notas / Descripción", type: "textarea", full: true },
  ],
  team_members: [
    { key: "name", label: "Nombre", required: true },
    { key: "role", label: "Cargo" },
    { key: "years_experience", label: "Años experiencia", type: "number" },
    { key: "imss", label: "IMSS" },
    { key: "allergies", label: "Alergias" },
    { key: "courses", label: "Cursos recibidos", type: "textarea", full: true },
  ],
};

export default function Communication({ embedded = false }) {
  const [tab, setTab] = useState("talks");
  return (
    <div data-testid="communication-module">
      {!embedded && (
        <div className="flex items-center justify-between mb-3">
          <h1 className="card-h1" style={{ margin: 0 }}>Competencia y Comunicación Ambiental</h1>
          <PDFButton endpoint="/pdf/module/racs" filename="reporte_comunicacion.pdf" label="Reporte PDF" />
        </div>
      )}
      <div className="tabs-bar">
        {TABS.map((t) => <button key={t.key} className={`tab ${tab === t.key ? "active" : ""}`} onClick={() => setTab(t.key)} data-testid={`com-tab-${t.key}`}>{t.label}</button>)}
      </div>
      {tab === "calendar" ? (
        <Calendar module="all" title="Calendario de actividades" testId="comm-calendar" />
      ) : tab === "inductions" ? (
        <InductionsModule />
      ) : (
        <CrudTable collection={tab} testIdPrefix={tab} fields={fieldsByColl[tab]} />
      )}
    </div>
  );
}

function InductionsModule() {
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <CrudTable
        collection="inductions"
        testIdPrefix="inductions"
        onRowClick={(row) => setSelected(row)}
        fields={fieldsByColl.inductions}
      />
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`Asistentes - ${selected?.title}`} size="xl" testId="induction-detail-modal">
        {selected && <InductionAttendees induction={selected} />}
      </Modal>
    </div>
  );
}

function InductionAttendees({ induction }) {
  const [attendees, setAttendees] = useState([]);
  const [showImport, setShowImport] = useState(false);

  async function load() {
    const { data } = await api.get("/data/induction_attendees", { params: { induction_id: induction.id } });
    setAttendees(data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [induction.id]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm">
          <Users size={14} style={{ verticalAlign: "middle" }} /> <b>{attendees.length} asistentes</b>
          {attendees.length > 0 && <> · ✅ {attendees.filter((a) => a.signed).length} firmaron · ❌ {attendees.filter((a) => !a.signed).length} pendientes</>}
        </div>
        <div className="flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={() => setShowImport(true)} data-testid="import-excel-btn">
            <Upload size={14} /> Importar desde Excel
          </button>
        </div>
      </div>
      <CrudTable
        collection="induction_attendees"
        filters={{ induction_id: induction.id }}
        parent={{ id: induction.id, field: "induction_id" }}
        testIdPrefix={`att-${induction.id}`}
        fields={[
          { key: "file_id", label: "# Ficha" },
          { key: "name", label: "Nombre completo", required: true },
          { key: "area", label: "Patio/Área" },
          { key: "department", label: "Contrato/Dpto" },
          { key: "type_entry", label: "Tipo ingreso", type: "select", options: ["Nuevo ingreso", "Reentrenamiento", "Visita", "Contratista", "Proveedor"] },
          { key: "signed", label: "Firmó lista", type: "checkbox" },
          { key: "has_warnings", label: "Aviso", type: "checkbox", render: (v) => v ? <span className="badge badge-yellow">⚠ Revisar</span> : "" },
        ]}
      />
      <ImportInductionAttendees
        inductionId={induction.id}
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={load}
      />
    </div>
  );
}
